import { DEFAULT_LOCALE, isLocale, type Locale } from '@/router/locales'

export type NavigationType = 'normal' | 'extended'

export type RawNavigationItem = {
  id?: number | string | null
  parent_id?: number | string | null
  object_id?: number | string | null
  object_type?: unknown
  type?: unknown
  label?: unknown
  url?: unknown
  relative_url?: unknown
  description?: unknown
  target?: unknown
  rel?: unknown
  classes?: unknown
  icon?: unknown
  meta?: unknown
  acf?: unknown
  children?: RawNavigationItem[] | null
}

export type RawNavigation = {
  id?: number | string | null
  slug?: unknown
  name?: unknown
  type?: unknown
  locations?: unknown
  locale?: unknown
  items?: RawNavigationItem[] | null
}

export type NavigationItem = {
  id: number
  parentId: number | null
  objectId: number | null
  objectType: string
  type: string
  label: string
  url: string
  relativeUrl: string
  description: string
  target: string
  rel: string
  classes: string[]
  icon: string | null
  children: NavigationItem[]
}

export type Navigation = {
  id: number
  slug: string
  name: string
  type: NavigationType
  locations: string[]
  locale: Locale
  items: NavigationItem[]
}

type RecordLike = Record<string, unknown>

function cleanString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : ''
}

function toRecord(value: unknown): RecordLike {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as RecordLike
    : {}
}

function positiveInteger(value: unknown): number | null {
  const number = Number(value ?? 0)

  return Number.isInteger(number) && number > 0 ? number : null
}

function stringList(value: unknown): string[] {
  if (!Array.isArray(value)) return []

  return [...new Set(value.map(cleanString).filter(Boolean))]
}

function normalizeType(value: unknown): NavigationType {
  return cleanString(value).toLowerCase() === 'extended' ? 'extended' : 'normal'
}

function resolveIcon(raw: RawNavigationItem): string | null {
  const meta = toRecord(raw.meta)
  const acf = toRecord(raw.acf)

  return cleanString(raw.icon) || cleanString(meta.icon) || cleanString(acf.icon) || null
}

export function mapNavigationItem(raw: RawNavigationItem): NavigationItem | null {
  const id = positiveInteger(raw.id)
  const label = cleanString(raw.label)

  if (id === null || !label) return null

  return {
    id,
    parentId: positiveInteger(raw.parent_id),
    objectId: positiveInteger(raw.object_id),
    objectType: cleanString(raw.object_type),
    type: cleanString(raw.type),
    label,
    url: cleanString(raw.url),
    relativeUrl: cleanString(raw.relative_url),
    description: cleanString(raw.description),
    target: cleanString(raw.target),
    rel: cleanString(raw.rel),
    classes: stringList(raw.classes),
    icon: resolveIcon(raw),
    children: (raw.children ?? []).flatMap((child) => {
      const item = mapNavigationItem(child)
      return item ? [item] : []
    }),
  }
}

export function mapNavigation(raw: RawNavigation, fallbackLocale: Locale): Navigation | null {
  const id = positiveInteger(raw.id)
  const slug = cleanString(raw.slug)
  const localeValue = cleanString(raw.locale)

  if (id === null || !slug) return null

  return {
    id,
    slug,
    name: cleanString(raw.name) || slug,
    type: normalizeType(raw.type),
    locations: stringList(raw.locations),
    locale: isLocale(localeValue) ? localeValue : fallbackLocale || DEFAULT_LOCALE,
    items: (raw.items ?? []).flatMap((item) => {
      const mapped = mapNavigationItem(item)
      return mapped ? [mapped] : []
    }),
  }
}

export function mapNavigations(raw: RawNavigation[], fallbackLocale: Locale): Navigation[] {
  return raw.flatMap((navigation) => {
    const mapped = mapNavigation(navigation, fallbackLocale)
    return mapped ? [mapped] : []
  })
}
